import { useEffect, useRef, type MutableRefObject } from "react";
import {
  useAudioVideo,
  useMeetingManager,
  useRosterState,
  useActiveSpeakersState,
  useToggleLocalMute,
} from "amazon-chime-sdk-component-library-react";
import {
  MeetingSessionStatus,
  MeetingSessionStatusCode,
  type ContentShareObserver,
  type VideoPriorityBasedPolicy,
} from "amazon-chime-sdk-js";
import { usePlatformStore } from "@platform/store";
import { ChimeObserverState, type ChimeObserverStore } from "./chimeObserverState";
import { useDeviceStateBroadcast } from "./useDeviceStateBroadcast";
import { useForegroundResume } from "./useForegroundResume";
import { logMeetingEvent } from "@core/features/videoMeeting/utils/logMeetingEvent";
import { resetQualityCollectorState } from "@core/features/videoMeeting/utils/collectCallQualityMetrics";

// Re-export for any consumers that import from this module
export { resolveAttendeeTileState } from "./resolveAttendeeTileState";

const DEVICE_STATE_TOPIC = "device-state";
const REACTION_TOPIC = "emoji-reaction";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Unified Chime observer hook. Registers one AudioVideoObserver, one
 * VideoDownlinkObserver, one data message subscription, and consumes
 * roster + active speaker hooks. All SDK events are translated into
 * `attendeeTileState` in Zustand.
 *
 * Delegates all state-machine logic to ChimeObserverState. This hook
 * is responsible only for React wiring: SDK hooks, effects, and cleanup.
 */
export function useChimeObserver(
  policyRef: MutableRefObject<VideoPriorityBasedPolicy>
) {
  const audioVideo = useAudioVideo();
  const meetingManager = useMeetingManager();
  const { roster } = useRosterState();
  const activeSpeakers = useActiveSpeakersState();
  const { muted } = useToggleLocalMute();
  const hasLocalVideo = usePlatformStore(
    (s) => s.videoSourceState.hasLocalVideo
  );
  const rosterSize = Object.keys(roster).length;

  // Synchronous roster ref for active speaker sync
  const rosterRef = useRef(roster);
  useEffect(() => {
    rosterRef.current = roster;
  }, [roster]);

  // --- State machine instance (lazy-init, stable across renders) ---

  const stateRef = useRef<ChimeObserverState | null>(null);
  if (!stateRef.current) {
    stateRef.current = new ChimeObserverState(
      () => {
        const s = usePlatformStore.getState();
        return {
          attendeeTileState: s.attendeeTileState,
          videoSourceState: s.videoSourceState,
          layout: s.layout,
          user: s.user,
          setAttendeeTileState: s.setAttendeeTileState,
          setAttendeeTileStates: s.setAttendeeTileStates,
          removeAttendeeTileState: s.removeAttendeeTileState,
          setConnectionQuality: s.setConnectionQuality,
          setActiveSpeakers: s.setActiveSpeakers,
          setRoster: s.setRoster,
          addReaction: s.addReaction,
          removeReaction: s.removeReaction,
          setState: usePlatformStore.setState,
        } satisfies ChimeObserverStore;
      },
      () =>
        meetingManager.meetingSession?.configuration.credentials?.attendeeId ||
        null
    );
  }
  const state = stateRef.current;
  // Bind the policy ref to the state instance so applyPreferenceUpdate can
  // call chooseRemoteVideoSources without each call site passing it in.
  // policyRef is stable across renders (useRef in useVideoDownlinkPolicy),
  // so this assignment is effectively once-per-mount.
  state.policyRef = policyRef;

  // -------------------------------------------------------------------------
  // Effect 1: Roster sync
  // -------------------------------------------------------------------------

  useEffect(() => {
    state.syncRoster(roster, state.getLocalAttendeeId());
  }, [roster, state]);

  // -------------------------------------------------------------------------
  // Effect 2: Active speaker sync
  // -------------------------------------------------------------------------

  useEffect(() => {
    state.syncActiveSpeakers(
      activeSpeakers,
      rosterRef.current,
      state.getLocalAttendeeId()
    );
  }, [activeSpeakers, state]);

  // Cleanup active speakers on unmount
  useEffect(() => {
    return () => {
      usePlatformStore.getState().setActiveSpeakers([]);
    };
  }, []);

  // -------------------------------------------------------------------------
  // Effect 3a: Synchronous presence subscription (roster departures)
  // -------------------------------------------------------------------------

  useEffect(() => {
    // Use the raw SDK audioVideo (not the component library proxy) because
    // the proxy may not expose realtimeUnsubscribeToAttendeeIdPresence.
    const rawAudioVideo = meetingManager.meetingSession?.audioVideo;
    if (!rawAudioVideo) return;
    return state.subscribeToPresence(rawAudioVideo);
  }, [meetingManager.meetingSession, state]);

  // -------------------------------------------------------------------------
  // Effect 3b: AudioVideoObserver + VideoDownlinkObserver registration
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!audioVideo) return;

    const store = usePlatformStore.getState();
    const capturedMeetingId = store.meetingId;
    const capturedRoomId = store.roomId;
    const capturedUser = store.user;

    const policy = policyRef.current;
    const stateObserver = state.createAudioVideoObserver();
    // Wrap the state machine's audioVideoDidStart with a one-shot
    // logMeetingEvent so we have a Redis signal that the SDK reached running
    // state. Distinct from sdk_meetingStartSucceeded (which fires from the
    // EventController, not the AudioVideoObserver) — the two callbacks have
    // different preconditions inside the SDK and we want to be able to tell
    // them apart in production. See plans/2026-05-04-video-webrtc-handshake-race-fix.md.
    const observer = {
      ...stateObserver,
      audioVideoDidStart: () => {
        logMeetingEvent({
          event_type: 'sdk_audioVideoDidStart',
          meeting_id: capturedMeetingId,
          room_id: capturedRoomId,
          user: capturedUser ? { id: capturedUser.id } : null,
        });
        stateObserver.audioVideoDidStart?.();
      },
    };
    const downlinkObserver = state.createDownlinkObserver(audioVideo);

    audioVideo.addObserver(observer);
    policy.addObserver(downlinkObserver);

    return () => {
      audioVideo.removeObserver(observer);
      policy.removeObserver(downlinkObserver);
      state.clearReconnectGuard();
      if (state.massDropTimer) {
        clearTimeout(state.massDropTimer);
        state.massDropTimer = null;
      }
      if (state.recoveryTimer) {
        clearTimeout(state.recoveryTimer);
        state.recoveryTimer = null;
      }
      // Drop the quality collector's per-SSRC duration priors so the first
      // sample of the next session reports null rather than diffing against
      // stale priors from this session's SSRCs. Cleanup runs whether the
      // hook unmounts or just gets a new audioVideo via the dep array — the
      // dep array on this effect includes `audioVideo`, so React fires this
      // before the next observer registration.
      resetQualityCollectorState();
    };
  }, [audioVideo, state, policyRef]);

  // -------------------------------------------------------------------------
  // Effect 4: Data message subscription (receive intent)
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!audioVideo) return;

    const deviceHandler = (message: any) => {
      state.handleDataMessage(message);
    };
    const reactionHandler = (message: any) => {
      state.handleReactionMessage(message);
    };

    audioVideo.realtimeSubscribeToReceiveDataMessage(
      DEVICE_STATE_TOPIC,
      deviceHandler
    );
    audioVideo.realtimeSubscribeToReceiveDataMessage(
      REACTION_TOPIC,
      reactionHandler
    );
    return () => {
      audioVideo.realtimeUnsubscribeFromReceiveDataMessage(DEVICE_STATE_TOPIC);
      audioVideo.realtimeUnsubscribeFromReceiveDataMessage(REACTION_TOPIC);
    };
  }, [audioVideo, meetingManager.meetingSession, state]);

  // -------------------------------------------------------------------------
  // Effect 4b: ContentShareObserver — diagnostic logging for share lifecycle
  // -------------------------------------------------------------------------
  //
  // Each callback emits one logMeetingEvent so admin Timeline can render a
  // SHARE row for the share lifecycle on the sharer's user hash. Note:
  // contentShareDidStart is gated on isSelfAttendee inside the SDK's
  // DefaultContentShareController, so these events are sharer-only — they
  // do NOT fire on viewer clients.
  useEffect(() => {
    if (!audioVideo) return;
    const store = usePlatformStore.getState();
    const capturedMeetingId = store.meetingId;
    const capturedRoomId = store.roomId;
    const capturedUser = store.user;

    const observer: ContentShareObserver = {
      contentShareDidStart: () => {
        logMeetingEvent({
          event_type: 'content_share_started',
          meeting_id: capturedMeetingId,
          room_id: capturedRoomId,
          user: capturedUser ? { id: capturedUser.id } : null,
          sharing_attendee_id: '',
        });
      },
      contentShareDidStop: () => {
        logMeetingEvent({
          event_type: 'content_share_stopped',
          meeting_id: capturedMeetingId,
          room_id: capturedRoomId,
          user: capturedUser ? { id: capturedUser.id } : null,
          sharing_attendee_id: '',
        });
      },
      contentShareDidPause: () => {
        logMeetingEvent({
          event_type: 'content_share_paused',
          meeting_id: capturedMeetingId,
          room_id: capturedRoomId,
          user: capturedUser ? { id: capturedUser.id } : null,
          sharing_attendee_id: '',
        });
      },
    };

    audioVideo.addContentShareObserver(observer);
    return () => audioVideo.removeContentShareObserver(observer);
  }, [audioVideo, meetingManager.meetingSession]);

  // -------------------------------------------------------------------------
  // Effect 5: Device state broadcast (extracted hook)
  // -------------------------------------------------------------------------

  const { broadcast } = useDeviceStateBroadcast(audioVideo, hasLocalVideo, muted, rosterSize);
  useForegroundResume({ broadcast, isEnabled: !!audioVideo });

  // -------------------------------------------------------------------------
  // Effect 6: SDK event capture (eventDidReceive via EventController)
  // -------------------------------------------------------------------------

  const SDK_EVENTS_TO_CAPTURE = new Set([
    'meetingStartSucceeded',
    'meetingStartFailed',
    'meetingFailed',
    'meetingEnded',
    'audioInputFailed',
    'videoInputFailed',
  ]);

  useEffect(() => {
    const eventController = meetingManager.meetingSession?.eventController;
    if (!eventController) return;

    // Capture meeting/room IDs at registration time so late-firing
    // events (meetingEnded, meetingFailed) still have context after
    // the Zustand store may have been reset.
    const store = usePlatformStore.getState();
    const capturedMeetingId = store.meetingId;
    const capturedRoomId = store.roomId;
    const capturedUser = store.user;

    const observer = {
      eventDidReceive: (name: string, attributes: Record<string, string>) => {
        if (!SDK_EVENTS_TO_CAPTURE.has(name)) return;

        // Enrich failure events with a parsed status_code (enum name) and
        // is_terminal bit so the admin log view can distinguish "SDK gave
        // up" from "SDK is about to retry" without re-interpreting the
        // opaque attributes blob. Only meetingStartFailed / meetingFailed
        // carry a meaningful meetingStatus.
        const enrichment: { status_code?: string; is_terminal?: boolean } = {};
        if (name === 'meetingStartFailed' || name === 'meetingFailed') {
          const raw = (attributes as Record<string, unknown>).meetingStatus;
          const numericCode = typeof raw === 'number'
            ? raw
            : parseInt(String(raw ?? ''), 10);
          if (!isNaN(numericCode)) {
            const codeName = MeetingSessionStatusCode[numericCode];
            if (typeof codeName === 'string') enrichment.status_code = codeName;
            try {
              enrichment.is_terminal = new MeetingSessionStatus(numericCode).isTerminal();
            } catch {
              // SDK internal threw; leave is_terminal absent rather than
              // lie. The admin view's banner treats missing as terminal
              // (safer default than hiding a real failure).
            }
          }
        }

        logMeetingEvent({
          event_type: `sdk_${name}`,
          meeting_id: capturedMeetingId,
          room_id: capturedRoomId,
          user: capturedUser ? { id: capturedUser.id } : null,
          sdk_event_name: name,
          sdk_attributes: attributes,
          ...enrichment,
        });
      },
    };

    eventController.addObserver(observer);
    return () => {
      eventController.removeObserver(observer);
    };
  }, [meetingManager.meetingSession]);

  // -------------------------------------------------------------------------
  // Cleanup all timers on unmount
  // -------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      state.dispose();
    };
  }, [state]);

  // Expose the state instance so sibling hooks (useLocalVideoSync,
  // useActiveSpeakerDownlinkSync) can call applyPreferenceUpdate /
  // notifyShareStart / notifyShareStop on it. The instance is stable
  // across renders (lazy-init in stateRef), so passing it through hook
  // returns is safe — no stale-closure concerns.
  return state;
}
